import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import { getCombinedClientNavItems } from 'src/models/platform/utils';
import { isTrainer } from 'src/models/user/utils';
import {
  clientRoutesExtraData,
  getDreamcoatReactRouteOverride,
  routesExtraData,
} from 'src/shared/AppFrame/routeConfig';
import { isDreamcoatActive } from 'src/shared/dreamcoat';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import toast from 'src/utils/toast';

import ErrorPage from './Error';

export const IndexPage = () => {
  const { session, user } = useOptionalSession();
  const [searchParams] = useSearchParams();

  const flash = searchParams.get('flash');

  useEffect(() => {
    if (flash) {
      toast.error(flash);
    }
  }, [flash]);

  if (!user?.id) {
    return <Navigate to="/login" replace />;
  }

  // For clients, use combined navigation items
  const navItems = isTrainer(user)
    ? session?.platformTabs['trainerDashboard']
    : getCombinedClientNavItems(session?.platformTabs);

  // Find first enabled menu item, excluding external links for clients
  let firstEnabledItem = navItems?.find(
    (item) =>
      item.enabled &&
      (isTrainer(user) || !clientRoutesExtraData[item.id]?.externalLink)
  );

  if (!firstEnabledItem) {
    return (
      <ErrorPage
        type={404}
        title="No Menu Items Available"
        message="There are no enabled menu items available for your account."
      />
    );
  }

  // For clients: Check if this is a parent menu with children (like "My Stuff")
  // If so, use the first enabled child item instead
  if (!isTrainer(user) && firstEnabledItem.id === 'My Stuff') {
    const clientAccountItems = session?.platformTabs['clientAccount'];
    const firstChildItem = clientAccountItems?.find((item) => item.enabled);
    if (firstChildItem) {
      firstEnabledItem = firstChildItem;
    }
  }

  // Similarly handle "Account" parent menu for trainers
  if (isTrainer(user) && firstEnabledItem.id === 'Account') {
    const trainerAccountItems = session?.platformTabs['trainerAccount'];
    const firstChildItem = trainerAccountItems?.find((item) => item.enabled);
    if (firstChildItem) {
      firstEnabledItem = firstChildItem;
    }
  }

  const dreamcoatFrame = isDreamcoatActive(session, user);
  let reactRoute = dreamcoatFrame
    ? firstEnabledItem.reactRoute ??
      getDreamcoatReactRouteOverride(firstEnabledItem.id, {
        client: !isTrainer(user),
      }) ??
      routesExtraData[firstEnabledItem.id]?.reactRoute
    : routesExtraData[firstEnabledItem.id]?.reactRoute;

  // Match useMainNav: these React routes are only valid when the feature flag
  // is on or Dreamcoat is active for the user.
  if (
    firstEnabledItem.id === 'Journal' &&
    (!session ||
      !(
        hasFeatureFlag(session, FeatureFlag.NewJournal) ||
        isDreamcoatActive(session, user)
      ))
  ) {
    reactRoute = undefined;
  }
  if (
    firstEnabledItem.id === 'Automations' &&
    (!session ||
      !(
        hasFeatureFlag(session, FeatureFlag.NewAutomations) ||
        isDreamcoatActive(session, user)
      ))
  ) {
    reactRoute = undefined;
  }

  // If first tab is Schedule and user has newCalendar, redirect to React calendar
  if (
    session &&
    firstEnabledItem.id === 'Schedule' &&
    (user?.newCalendar || dreamcoatFrame)
  ) {
    reactRoute = '/calendar';
  }

  if (
    session &&
    firstEnabledItem.id === 'Dashboard' &&
    !hasFeatureFlag(session, FeatureFlag.NewDashboard) &&
    !user?.newDashboard
  ) {
    // Redirect to old dashboard
  } else if (reactRoute) {
    return <Navigate to={reactRoute} replace />;
  }

  // Use the original route with /embed/ prefix
  let route = firstEnabledItem.route.replace(/^\//, ''); // Remove leading slash if present

  if (route.startsWith('dashboard/fbm/schedule')) route = 'fbm/schedule/';

  return <Navigate to={`/embed/${route}`} replace />;
};
